/**
 * FinanceFuelExpensePanel — Finance & Accounting fuel expenses tab.
 * Read-only feed of fuel receipts filed by field staff, filterable by
 * staff / vehicle / department / date range.
 */
import { useEffect, useState, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Fuel } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FinanceFuelExpensePanel() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ staff_user_id: '', vehicle_id: '', department: '', date_from: '', date_to: '' });

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
    const r = await fetch(`${API}/api/procurement/vehicles/fuel-receipts?${q.toString()}`, { credentials: 'include' });
    if (r.ok) setRows(await r.json());
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const total = rows.reduce((acc, r) => acc + (Number(r.total_amount) || 0), 0);
  const gallons = rows.reduce((acc, r) => acc + (Number(r.gallons) || 0), 0);

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 mt-4" data-testid="finance-fuel-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Fuel className="h-5 w-5" style={{ color: '#22c55e' }} />
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Fuel Expenses
          </h3>
        </div>
        <div className="text-right">
          <p className="text-[11px] text-zinc-500 uppercase tracking-widest">Total</p>
          <p className="text-lg font-bold text-emerald-400" data-testid="fuel-total-sum">${total.toFixed(2)} · {gallons.toFixed(1)} gal</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-6 gap-2 mb-3">
        <div><Label className="text-[10px] text-zinc-400 uppercase">From</Label><Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" /></div>
        <div><Label className="text-[10px] text-zinc-400 uppercase">To</Label><Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" /></div>
        <div><Label className="text-[10px] text-zinc-400 uppercase">Staff ID</Label><Input value={filters.staff_user_id} onChange={(e) => setFilters((f) => ({ ...f, staff_user_id: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" /></div>
        <div><Label className="text-[10px] text-zinc-400 uppercase">Vehicle ID</Label><Input value={filters.vehicle_id} onChange={(e) => setFilters((f) => ({ ...f, vehicle_id: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" /></div>
        <div><Label className="text-[10px] text-zinc-400 uppercase">Department</Label><Input value={filters.department} onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" /></div>
        <div className="flex items-end"><Button variant="secondary" onClick={load} className="w-full">Apply</Button></div>
      </div>
      <table className="admin-stack-table text-sm" data-testid="finance-fuel-table">
        <thead>
          <tr className="text-[11px] uppercase text-zinc-500 border-b border-zinc-800">
            <th className="text-left py-2 pr-3">Date</th>
            <th className="text-left py-2 pr-3">Vehicle</th>
            <th className="text-left py-2 pr-3">Staff</th>
            <th className="text-left py-2 pr-3">Station</th>
            <th className="text-left py-2 pr-3">Gallons</th>
            <th className="text-right py-2 pr-3">Total</th>
            <th className="text-left py-2 pl-3">Card</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.receipt_id} className="border-b border-zinc-800/60 text-zinc-200">
              <td className="py-3 pr-3" data-label="Date">{r.date}</td>
              <td className="py-3 pr-3 font-mono text-[#FFD60A]" data-label="Vehicle">{r.vehicle_code}</td>
              <td className="py-3 pr-3" data-label="Staff">
                <div>{r.name}</div>
                <div className="text-[11px] text-zinc-500">{r.position}</div>
              </td>
              <td className="py-3 pr-3" data-label="Station">{r.station}</td>
              <td className="py-3 pr-3" data-label="Gallons">{r.gallons}</td>
              <td className="py-3 pr-3 text-right text-emerald-400 font-semibold" data-label="Total">${Number(r.total_amount).toFixed(2)}</td>
              <td className="py-3 pl-3 font-mono text-xs" data-label="Card">•••• {r.gas_card_last4 || '····'}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={7} className="text-center text-zinc-500 py-4">No fuel receipts filed.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}
